import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RouteProps, useParams } from 'react-router-dom';

import { useServerData } from '@xingternal/hops';
import { BodyCopy } from '@xingternal/typography';

import { PageError } from '@/components/PageError';
import { CompanyLoggedout } from '@/components/CompanyLoggedout';
import { useCompaniesByIndustry } from '@/hooks/useCompaniesByIndustry';
import { usePagination } from '@/hooks/usePagination';
import { useTrackPage } from '@/hooks/useTrackPage';

import { getIndustryName } from '@/utils/url';
import { FIELD_SCRAMBLING_ERROR, RESOLVE_ERROR } from '@/config/constants';

import { scrollToTop } from '@/utils';
import { pagesTrackingName } from '@/utils/tracking';
import { Company } from '@/types/graphql-types';
import styles from './IndustryCompanies.css';

const getErrorCode = (errorId: string) =>
  errorId === FIELD_SCRAMBLING_ERROR || errorId === RESOLVE_ERROR
    ? '404'
    : '503';

type IndustryParams = {
  industry: string;
};

const IndustryByCompanies: React.FC<RouteProps> = () => {
  let { industry } = useParams<IndustryParams>();
  const intl = useIntl();
  useEffect(() => scrollToTop(), []);
  const { authenticated } = useServerData();
  const INDUSTRIES_LIMIT = 80;

  const { changePage, activePage } = usePagination();
  const id = industry.split('-')[0];
  const { data, loading, error } = useCompaniesByIndustry({
    id,
    limit: INDUSTRIES_LIMIT,
    offset: INDUSTRIES_LIMIT * (activePage - 1),
  });
  useTrackPage({ pageName: pagesTrackingName.industry });

  if (error) {
    const errorId = error?.graphQLErrors?.[0]?.message;
    let errorCode = getErrorCode(errorId);

    return <PageError errorCode={errorCode} />;
  }

  const { collection, total } = data?.companiesByIndustry ?? {};
  const industryName = getIndustryName({ id, intl });

  return (
    <CompanyLoggedout
      companies={collection?.map((item) => item?.company as Company)}
      total={total}
      headerText={intl.formatMessage(
        { id: 'CP_100_DIRECTORY_TITLE_INDUSTRY' },
        { industry: industryName },
      )}
      secondaryHeader={
        <BodyCopy size="medium">
          <FormattedMessage
            id="CP_100_DIRECTORY_INTRO_INDUSTRY"
            values={{ industry: industryName }}
          />
        </BodyCopy>
      }
      authenticated={authenticated}
      limit={INDUSTRIES_LIMIT}
      activePage={activePage}
      changePage={changePage}
      loading={loading}
      error={error}
      styles={styles}
    />
  );
};

export { IndustryByCompanies };
